import { memo } from 'react'

/**
 * GlassmorphButton - Atomic, reusable button with glassmorphism design
 * 
 * Features:
 * - Glassmorphism aesthetic (backdrop blur, semi-transparent)
 * - Multiple variants: icon-only, primary, spot, secondary, danger, ghost
 * - Consistent hover/active states
 * - Badge support
 * - Size variants
 * 
 * @param {Object} props
 * @param {React.Component} props.icon - Heroicon component (optional)
 * @param {string} props.label - Button text (optional, for screen readers)
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.isActive - Active state styling
 * @param {number|string} props.badge - Badge count/text (optional)
 * @param {string} props.variant - 'icon'|'primary'|'spot'|'secondary'|'danger'|'ghost' (default: 'icon')
 * @param {string} props.size - 'sm'|'md'|'lg' (default: 'md')
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional classes
 * @param {React.ReactNode} props.children - Button content (if not using icon)
 * 
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render button
 * 
 * @example
 * // Icon button (for tabs/actions)
 * <GlassmorphButton 
 *   icon={MagnifyingGlassIcon} 
 *   label="Search" 
 *   onClick={handleClick}
 *   isActive={activeTab === 'search'}
 * />
 * 
 * @example
 * // Primary action button
 * <GlassmorphButton 
 *   variant="primary"
 *   onClick={handleSubmit}
 * >
 *   Submit Form
 * </GlassmorphButton>
 */
const GlassmorphButton = memo(function GlassmorphButton({
  icon: Icon,
  label,
  onClick,
  isActive = false,
  badge = null,
  variant = 'icon',
  size = 'md',
  disabled = false,
  className = '',
  children,
  ...restProps
}) {
  // Size classes
  const sizeClasses = {
    xs: 'p-1',
    sm: 'p-1.5',
    mobile: 'p-2',
    md: 'p-2.5',
    lg: 'p-3.5'
  }
  
  const iconSizeClasses = {
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    mobile: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  }
  
  // Variant classes
  const variantClasses = {
    // Icon-only buttons (tabs, actions)
    icon: isActive
      ? 'bg-primary text-white shadow-md scale-105 border border-primary'
      : 'bg-white/90 text-gray-700 hover:bg-white hover:scale-105 hover:shadow-lg backdrop-blur-sm border border-gray-200 hover:border-primary/50',
    
    // Primary action buttons
    primary: isActive
      ? 'bg-primary-600 text-white shadow-md scale-105 ring-2 ring-primary/30 border border-primary-700'
      : 'bg-primary text-white hover:bg-primary-600 shadow-sm hover:shadow-md border border-primary-700/20',
    
    // Spot Mode button (solid primary color matching other action buttons)
    spot: 'bg-primary text-white hover:bg-primary-600 shadow-sm hover:shadow-md border border-primary-700/20 hover:scale-105',
    
    // Secondary action buttons  
    secondary: 'bg-white/90 text-gray-700 hover:bg-white hover:shadow-md backdrop-blur-sm border border-gray-200/50',
    
    // Danger/destructive buttons
    danger: 'bg-red-500/90 text-white hover:bg-red-600 shadow-sm hover:shadow-md border border-red-600/20',
    
    // Ghost buttons (minimal style)
    ghost: 'text-gray-700 hover:bg-gray-100/80 border border-transparent hover:border-gray-200/50'
  }
  
  // Base classes
  const baseClasses = `
    rounded-lg
    transition-all duration-200
    flex items-center justify-center
    relative
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${disabled ? '' : 'cursor-pointer'}
    ${className}
  `.trim().replace(/\s+/g, ' ')
  
  return (
    <button
      onClick={onClick}
      className={baseClasses}
      title={label}
      aria-label={label}
      disabled={disabled}
      {...restProps}
    >
      {Icon && <Icon className={iconSizeClasses[size]} />}
      {Icon && children && <span className="w-2" />}
      {children}
      
      {/* Notification badge */}
      {badge && (
        <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
          {typeof badge === 'number' && badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  )
})

export default GlassmorphButton
